<template>
    <form
        class="form basic-form location-form"
        autocomplete="off"
        novalidate
        @submit="emitSubmit"
    >
        <!-- name -->
        <div class="form-field">
            <label for="name" class="required">Name:</label>
            <input
                id="name"
                v-model="form.name.value"
                type="text"
                maxlength="100"
            />
            <FormError :errors="errors.name" />
        </div>

        <!-- date & nights -->
        <div class="two-fields">
            <div class="form-field">
                <label for="date" class="required">Start date:</label>
                <input
                    id="date"
                    v-model="form.date.value"
                    type="date"
                    :min="minDate"
                    :max="maxDate"
                />
                <FormError :errors="errors.date" />
            </div>
            <div class="form-field">
                <label for="nights" class="required">Nights:</label>
                <input id="nights" v-model="form.nights.value" type="number" />
                <FormError :errors="errors.nights" />
            </div>
        </div>

        <!-- geo -->
        <div class="form-field">
            <label for="geo" class="required">
                Find geo (search by city, town, village etc.):
            </label>
            <GeoSearchInput
                id="geo"
                ref="geo"
                v-model="form.geo.value"
                level="city"
                @update:model-value="fillNameFromGeo"
            />
            <FormError :errors="errors.geo" />
        </div>

        <!-- companions -->
        <div v-if="!tripCompanions.length" class="form-field">
            <label for="companions">Travel companions:</label>
            <MultiselectableDropdown
                id="companions"
                v-model="form.companions.value"
                insertable
                typable
                new-prompt-text="Start adding companions by typing in their names"
                :options="companionOptions"
                :loaded="formValuesSet"
            ></MultiselectableDropdown>
            <small>You can adjust companions per location.</small>
            <FormError :errors="errors.companions" />
        </div>
        <div v-if="tripCompanions.length" class="form-field">
            <div class="label">Trip companions:</div>
            <ul class="comma-separated">
                <li v-for="item in tripCompanions" :key="item.id">
                    {{ item.name }}
                </li>
            </ul>
            <small>
                You can unset companions on the trip if you'd like to track
                companions separately on each location.
            </small>
        </div>

        <!-- notes -->
        <div class="form-field">
            <label for="notes">Notes:</label>
            <textarea id="notes" v-model="form.notes.value" rows="4" />
            <FormError :errors="errors.notes" />
        </div>

        <!-- host -->
        <div class="form-field">
            <label for="host">Host:</label>
            <input
                id="host"
                v-model="form.host.value"
                type="text"
                maxlength="100"
            />
            <FormError :errors="errors.host" />
        </div>

        <div v-if="!showMot" class="form-field">
            <span
                style="text-decoration: underline; cursor: pointer"
                @click="showMot = true"
            >
                Do you track means of transport on your trips?
            </span>
        </div>

        <!-- mot -->
        <div v-if="showMot" class="two-fields">
            <div class="form-field">
                <label for="mot">Transport:</label>
                <select
                    id="mot"
                    v-model="form.mot.value"
                    @change="markUpdated('mot')"
                >
                    <EnumOptions
                        group="transport"
                        nullable
                        :null-label="impliedMotLabel"
                    />
                </select>
                <small v-if="impliedMotLabel">
                    Leave it empty to use the trip's transport on the way there.
                </small>
                <FormError :errors="errors.mot" />
            </div>
            <div class="form-field">
                <label for="mot-count">Number of transfers:</label>
                <input
                    id="mot-count"
                    v-model="form.mot_count.value"
                    type="number"
                />
                <small>
                    For day trips, you can use this field to count the total
                    number of rides (way there and back).
                </small>
                <FormError :errors="errors.mot_count" />
            </div>
        </div>

        <div v-if="!showExpenses" class="form-field">
            <span
                style="text-decoration: underline; cursor: pointer"
                @click="showExpenses = true"
            >
                Do you track expenses on your locations?
            </span>
            <br />
            <small>
                Location expenses don't count toward total trip expenses.
            </small>
        </div>

        <!-- expenses -->
        <h3 v-if="showExpenses" class="field-title">Expenses</h3>
        <div v-if="showExpenses" class="two-fields">
            <div class="form-field">
                <label for="expenses">Total:</label>
                <input
                    id="expenses"
                    v-model="form.expenses.value"
                    type="number"
                />
                <FormError :errors="errors.expenses" />
            </div>
            <div class="form-field">
                <label for="share">Your share (between 0 & 1):</label>
                <input
                    id="share"
                    v-model="form.share.value"
                    type="number"
                    max="100"
                />
                <small>
                    If you didn't share this expense, you should leave it at 1.
                </small>
                <FormError :errors="errors.share" />
            </div>
        </div>

        <!-- images -->
        <div class="form-field">
            <label for="image">Image:</label>
            <ImageUpload
                v-model="form.user_image_file.value"
                name="image"
                :img="location && location.user_image_url"
            />
            <FormError :errors="errors.user_image_file" />
        </div>
        <div
            v-if="form.geo.value && (!location || !location.user_image_url)"
            class="form-field"
        >
            <label for="image">Alternative image:</label>
            <WikiImagePicker
                v-model="form.wiki_image_url.value"
                :geo="form.geo.value"
            />
            <FormError :errors="errors.wiki_image_url" />
        </div>

        <div class="actions">
            <div v-if="deletePending" class="confirmation">
                Are you sure you want to delete this location?
            </div>
            <Bttn v-if="!deletePending" id="submit-location" type="submit">
                Save
            </Bttn>
            <Bttn
                v-if="canDelete && !deletePending"
                class="outlined"
                @click="deletePending = true"
            >
                Delete
            </Bttn>
            <Bttn
                v-if="deletePending"
                class="outlined"
                @click="deletePending = false"
            >
                Cancel
            </Bttn>
            <Bttn v-if="deletePending" @click="emitDelete">Confirm</Bttn>
            <router-link v-if="!deletePending" :to="backLink">
                <Bttn class="outlined">Back</Bttn>
            </router-link>
        </div>
    </form>
</template>

<script>
import BaseForm from "js/components/base/BaseForm";
import GeoSearchInput from "js/components/forms/GeoSearchInput";
import EnumOptions from "js/components/forms/EnumOptions";
import FormError from "js/components/forms/FormError";
import WikiImagePicker from "js/components/forms/WikiImagePicker";
import ImageUpload from "js/components/forms/ImageUpload";
import { useTripStore } from "js/stores/tripStore";
import { useUserStore } from "js/stores/userStore";
import { useUtilsStore } from "js/stores/utilsStore";
import { formatDateISO } from "js/lib/dates";

export default {
    name: "LocationForm",
    components: {
        GeoSearchInput,
        FormError,
        EnumOptions,
        ImageUpload,
        WikiImagePicker,
    },
    extends: BaseForm,
    props: {
        backLink: {
            type: String,
            required: true,
        },
        location: {
            type: Object,
            default: undefined,
        },
        tripId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            minDate: undefined,
            maxDate: undefined,
            tripStore: undefined,
            userStore: undefined,
            utilsStore: undefined,
            showMot: false,
            showExpenses: false,
            companionOptions: [],
            tripCompanions: [],
            tripStartMot: null,
            tripLocations: [],
            autoFilledName: null,
            form: {
                name: {
                    value: null,
                },
                date: {
                    value: null,
                    convert: formatDateISO,
                },
                nights: {
                    value: 0,
                },
                companions: {
                    value: [],
                },
                notes: {
                    value: null,
                },
                host: {
                    value: null,
                },
                impression: {
                    value: null,
                },
                mot: {
                    value: null,
                },
                mot_count: {
                    value: 1,
                },
                expenses: {
                    value: null,
                },
                share: {
                    value: 1,
                },
                geo: {
                    value: null,
                },
                wiki_image_url: {
                    value: null,
                },
                user_image_file: {
                    value: null,
                },
            },
        };
    },
    computed: {
        isFirstLocation() {
            const others = this.tripLocations.filter(
                (item) => item.id !== this.location?.id
            );
            if (!others.length) {
                return true;
            }
            const date = this.form.date.value;
            if (!date) {
                return false;
            }
            const earliest = others
                .map((item) => formatDateISO(item.date))
                .sort()[0];
            if (date !== earliest) {
                return date < earliest;
            }
            return (
                !!this.location &&
                formatDateISO(this.location.date) === date &&
                this.tripLocations[0].id === this.location.id
            );
        },
        impliedMotLabel() {
            if (!this.tripStartMot || !this.isFirstLocation) {
                return "";
            }
            return `${this.$enumGet("transport", this.tripStartMot)} (from trip)`;
        },
    },
    mounted() {
        this.tripStore = useTripStore();
        this.utilsStore = useUtilsStore();
        this.userStore = useUserStore();
        this.utilsStore.companions.then((companions) => {
            this.companionOptions = companions;
        });
        this.userStore.refreshUser().then(() => {
            this.showExpenses =
                this.userStore.user.preferences.track_location_expenses;
            this.showMot = this.userStore.user.preferences.track_mot;
        });
        this.loadConstraints();
    },
    methods: {
        loadGeos(name) {
            if (this.form.geo.value !== null) {
                return;
            }
            this.$refs.geo.preload(name);
        },
        fillNameFromGeo(geo) {
            const geoName = geo?.name || geo?.city;
            if (!geoName) {
                return;
            }
            const name = this.form.name.value;
            if (name?.trim() && name !== this.autoFilledName) {
                return;
            }
            this.form.name.value = geoName;
            this.autoFilledName = geoName;
        },
        async loadConstraints() {
            const storedTrip = this.tripStore.find(this.tripId);
            const trip =
                storedTrip ??
                (await this.tripStore.fetch(`/api/trips/${this.tripId}`));
            this.minDate = formatDateISO(trip.start_date);
            this.maxDate = formatDateISO(trip.end_date ?? undefined);
            this.tripCompanions = trip.companions || [];
            this.tripStartMot = trip.start_mot ?? null;
            if (this.tripStartMot) {
                await this.loadTripLocations(storedTrip);
            }
        },
        async loadTripLocations(storedTrip) {
            const hasFullLocations =
                storedTrip && !this.tripStore.hasDirtyLocations(this.tripId);
            this.tripLocations = hasFullLocations
                ? storedTrip.locations
                : await this.tripStore.fetch(
                      `/api/trips/${this.tripId}/locations`
                  );
        },
    },
};
</script>
